/* eslint-disable @typescript-eslint/no-explicit-any */
import { CoreService } from '@core/services/core.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, switchMap, throwError } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { AuthHeaderNames, httpHeaders } from '@dgx-pr-spa/shared/util';
import { catchError, filter, take, tap } from 'rxjs/operators';
import { ENVIRONMENT } from '@dgx-pr-spa/ui-components';
import { TokenService } from '@shared/services/token.service';

@Injectable({
  providedIn: 'root',
})
export class RequestInterceptorService implements HttpInterceptor {
  constructor(
    public readonly coreService: CoreService,
    @Inject(ENVIRONMENT) private environment: any,
    private readonly tokenService: TokenService
  ) {
    this.coreService.httpState$
      .pipe(
        filter(({ loaded }) => loaded),
        take(1)
      )
      .subscribe((state) => {
        this.correlationId = state.data;
      });
  }

  correlationId: string;

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();

    return undefined;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.tokenService.resetUrlRetryCount(req.url);

    if (req.url.includes('api.pr.e2e')) {
      this.tokenService.regenerateToken(req.url);
      const cookie = this.getCookie('ASC');

      let clonedHeaders = req.headers.append(
        'Content-Type',
        'application/json'
      );

      if (cookie?.length) {
        clonedHeaders = clonedHeaders.append('ASC', cookie);
        clonedHeaders = clonedHeaders.append('x-recaptcha-token', 'UNSET');
        const newReq = req.clone({ headers: clonedHeaders });

        return next.handle(newReq);
      }

      return this.tokenService.getToken(req.url).pipe(
        switchMap((token) => {
          clonedHeaders = clonedHeaders.append('ASC', 'UNSET');
          clonedHeaders = clonedHeaders.append('x-recaptcha-token', token);
          const newReq = req.clone({
            headers: clonedHeaders,
            withCredentials: true,
          });
          return next.handle(newReq).pipe(
            tap((val) => {
              // Use this when Set-Cookie does not work
              if ((val as HttpResponse<any>).body?.asc) {
                if (this.getCookie('ASC')) return;
                const expires = new Date(
                  Date.now() + 6 * 60 * 60 * 1000
                ).toUTCString();
                document.cookie = `ASC=${
                  (val as HttpResponse<any>).body.asc
                }; expires=${expires}; path=/`;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              if (
                error.status === 401 &&
                this.tokenService.getUrlRetryCount(req.url) < 2
              ) {
                this.tokenService.retryingRequest(req.url);
              }

              return throwError(() => new HttpErrorResponse(error));
            })
          );
        })
      );
    }

    return this.getRequestObservable(req, next);
  }

  getRequestObservable(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const apiWithUnAuthenticatedHandling = () => {
      return this.tokenService.getToken(req.url).pipe(
        switchMap((token) => {
          let newHeaders = this.correlationId
            ? req.headers.append(
                httpHeaders.X_CORRELATION_ID,
                this.correlationId
              )
            : req.headers;

          if (!req.url.includes(`/config.json`)) {
            newHeaders = this.addAuthHeaders(newHeaders, token);
          }

          const newReq = req.clone({ headers: newHeaders });
          return next.handle(newReq).pipe(
            tap((val) => {
              // Use this when Set-Cookie does not work
              if ((val as HttpResponse<any>).body?.asc) {
                const expires = new Date(
                  Date.now() + 6 * 60 * 60 * 1000
                ).toUTCString();
                document.cookie = `ASC=${
                  (val as HttpResponse<any>).body.asc
                }; expires=${expires}; path=/`;
              }
            }),
            catchError((error: HttpErrorResponse) => {
              if (
                error.status === 401 &&
                this.tokenService.getUrlRetryCount(req.url) < 2
              ) {
                this.tokenService.retryingRequest(req.url);

                return apiWithUnAuthenticatedHandling();
              }

              return throwError(() => new HttpErrorResponse(error));
            }),
            tap(() => this.tokenService.regenerateToken(req.url))
          );
        })
      );
    };

    return apiWithUnAuthenticatedHandling();
  }

  addAuthHeaders(newHeaders: HttpHeaders, token: string): HttpHeaders {
    return newHeaders
      .append(httpHeaders.X_RECAPTCHA_TOKEN, token)
      .append(
        AuthHeaderNames.CLIENT,
        this.environment.requestSource || 'DandGUK'
      )
      .append(
        AuthHeaderNames.REQUEST_ACTION,
        this.environment.requestAction || 'PR'
      )
      .append(
        AuthHeaderNames.REQUEST_SOURCE,
        this.environment.requestSource || 'DandGUK'
      );
  }
}
